/*
|-------------------------------------------------------------------------------
| Shell Window
|-------------------------------------------------------------------------------
|
| rex-app assumes it is the top-level browser window: it reads `window.parent`
| (and occasionally `window.top`) to reach the shell's storage, location, etc.
| That holds when rex-app runs standalone (parent === self) and from within the
| same-origin classic frame (parent === the shell).
|
| When rex-app is embedded inside the Rex Lobby, the shell is no longer the
| top window — `window.parent` / `window.top` *from the shell* are the Lobby's
| window, which rex-app must never read or write:
|
|   Lobby window (top)          ← never rex-app's; never returned
|   └── rex-app shell iframe    ← stamped by markShellWindow() at boot
|       └── classic frame       ← rex-app's own nested iframe
|
| This is the Lobby contract's window chokepoint: we mark rex-app's own
| outermost window (the shell) once at boot, and getShellWindow() resolves it
| from either the shell context or the nested classic-frame context. Note the
| classic frame's parent is the shell, not the Lobby — so a `window.parent`
| read is only ever a Lobby read when made from the shell itself, and that is
| exactly the context the stamp short-circuits. It returns rex-app's own top
| window, never the Lobby's.
|
| Standalone behaviour is unchanged: getShellWindow() returns the same window
| the old `window.parent` did.
|
| This file is plain .js (types in shell-window.d.ts) because classic's
| browserify bundle pulls it in via shared-modules.js, and that chain resolves
| only .js — see classic/gulp/tasks/modules.js.
*/

const SHELL_FLAG = '__REX_SHELL_WINDOW__';

/** Mark the current window as rex-app's own top window. Call once, first thing at boot. */
export function markShellWindow() {
  try {
    window[SHELL_FLAG] = true;
  } catch (e) {
    // ignore — best effort
  }
}

/**
 * Resolve rex-app's own top window (the shell) from the current context.
 * - Shell context: this window carries the boot stamp, so it is the shell —
 *   returned before `window.parent` (embedded: the Lobby) is ever consulted.
 * - Classic-frame context: our (same-origin) parent is the shell — embedded,
 *   the classic frame sits inside the shell's iframe, so its parent is still
 *   rex-app's shell, not the Lobby.
 * - Fallback (tests, full-page classic screens loaded outside the shell, or
 *   code somehow running before the boot mark): this window itself. Never
 *   `window.parent` — unstamped and nested, parent may be the Lobby, and a
 *   missing stamp must degrade to "the chokepoint stops resolving upward",
 *   never escalate to reading or mutating the host application.
 */
export function getShellWindow() {
  if (window[SHELL_FLAG]) return window;
  try {
    if (window.parent && window.parent[SHELL_FLAG]) return window.parent;
  } catch (e) {
    // cross-origin parent access — fall through
  }
  if (window.parent && window.parent !== window) {
    // Unstamped but nested: a boot-order regression this fallback would
    // otherwise silence. Report once so it surfaces instead of failing quietly.
    if (!window.__REX_SHELL_WINDOW_FALLBACK_REPORTED__) {
      window.__REX_SHELL_WINDOW_FALLBACK_REPORTED__ = true;
      window.bugsnagClient &&
        window.bugsnagClient.notify(
          new Error(
            'getShellWindow: nested window has no shell stamp in reach — falling back to self'
          )
        );
    }
  }
  return window;
}

/**
 * The path prefix the Lobby serves rex-app under ('' standalone). Set at boot
 * by mark-shell-window from the /rexapp prefix; the patched whereabouts keys
 * off the same global.
 */
export function getLobbyBasename() {
  try {
    return getShellWindow().__REX_WHEREABOUTS_BASENAME__ || '';
  } catch (e) {
    return '';
  }
}

/**
 * Whether rex-app is hosted by the Rex Lobby: nested inside another window
 * *and* served under the Lobby's path prefix (set at boot by
 * mark-shell-window). Nesting alone is not enough — Cypress, for one, runs
 * the app inside an iframe — so Lobby-only behaviour must key off both.
 * Resolves from the shell window, so it is false standalone even when called
 * from inside the classic frame — that nesting is rex-app's own.
 */
export function isLobbyHosted() {
  const shell = getShellWindow();
  return shell.self !== shell.top && !!getLobbyBasename();
}

/**
 * Add the Lobby's basename to a rooted url, for writes that bypass the patched
 * whereabouts history — e.g. hard redirects assigning `location.href` —
 * which would otherwise leave the /rexapp proxy namespace. Mirrors the patch's
 * own re-add on write: inspects only the path portion (ignoring ?#) so an
 * already-prefixed url is never double-prefixed. Identity standalone.
 */
export function addLobbyBasename(url) {
  const base = getLobbyBasename();
  if (!base || !url || url.charAt(0) !== '/') return url;
  const queryOrHashIndex = url.search(/[?#]/);
  const path = queryOrHashIndex === -1 ? url : url.slice(0, queryOrHashIndex);
  const rest = queryOrHashIndex === -1 ? '' : url.slice(queryOrHashIndex);
  if (path === base || path.indexOf(`${base}/`) === 0) return url;
  return path === '/' ? base + rest : base + url;
}

/**
 * Strip the Lobby's basename from a rooted url (path, or path + search/hash),
 * so raw `location` reads compare like-for-like with the router's own
 * basename-free paths. Identity standalone.
 */
export function stripLobbyBasename(url) {
  const base = getLobbyBasename();
  if (!base || !url || url.indexOf(base) !== 0) return url;
  const next = url.charAt(base.length);
  // Only when the prefix is the whole segment — never '/rexappanything'.
  if (next !== '' && next !== '/' && next !== '?' && next !== '#') return url;
  const rest = url.slice(base.length);
  return rest.charAt(0) === '/' ? rest : `/${rest}`;
}

/**
 * Open a rooted app url in a new tab/window, keeping it inside the Lobby's
 * proxy namespace when embedded. `window.open` with a router path bypasses
 * the patched history, so the /rexapp prefix must be re-added here. Identity
 * standalone; non-rooted urls pass through untouched.
 */
export function openAppUrl(path, target, features) {
  return getShellWindow().open(addLobbyBasename(path), target, features);
}
